import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, XCircle, HelpCircle, Loader2, CalendarClock } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { ApiResponse, DomainRenewal, RenewalQuoteLine } from '../types';
import { registrarName } from '../lib/registrar-names';
import Modal from './ui/Modal';

/** How often to poll a background (bulk) batch. */
const POLL_MS = 2000;
const OPEN_STATUSES = new Set(['queued', 'processing']);

const money = (amount: number | null | undefined, currency: string | null | undefined) =>
  amount == null ? '—' : `${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency ?? ''}`.trim();

const day = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : '—');

type Phase = 'quoting' | 'review' | 'running' | 'done' | 'error';

/** Terms offered; registries cap registrations at 10 years from today. */
const TERMS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const yrs = (n: number) => `${n} year${n === 1 ? '' : 's'}`;

/**
 * Renew one or more domains for 1–10 years: quote → explicit confirmation →
 * outcome. Renewing charges each domain's registrar account, so the user sees
 * the live price per domain and the total before anything is spent, and must
 * tick an acknowledgement. One domain renews inline; several run in the
 * background and this polls the batch.
 */
export default function RenewModal({ domainIds, onClose, onDone }: {
  domainIds: string[];
  onClose: () => void;
  /** Called after renewals ran (any outcome), so the caller can reload. */
  onDone?: () => void;
}) {
  const { fetchApi } = useApi();
  const [phase, setPhase] = useState<Phase>('quoting');
  const [error, setError] = useState<string | null>(null);
  const [quotes, setQuotes] = useState<RenewalQuoteLine[]>([]);
  const [totals, setTotals] = useState<Record<string, number>>({});
  const [ack, setAck] = useState(false);
  const [results, setResults] = useState<DomainRenewal[]>([]);
  const [rejected, setRejected] = useState<{ id: string; reason: string }[]>([]);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  // The domains are fixed when the modal opens. The parent's selection can
  // change underneath it (a list reload clears it), and re-quoting would
  // silently replace the quote being reviewed.
  const [ids] = useState(() => [...domainIds]);
  const [years, setYears] = useState(1);

  // Quote once per chosen term. React StrictMode runs effects twice in
  // development, and each quote request creates renewal quotes server-side,
  // so a ref remembers the term already quoted, and a mounted ref (not a
  // per-run cancelled flag) decides whether to apply a result. A response for
  // a term that is no longer selected is dropped.
  const quotedYearsRef = useRef<number | null>(null);
  const yearsRef = useRef(years);
  yearsRef.current = years;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (quotedYearsRef.current === years) return;
    quotedYearsRef.current = years;
    const forYears = years;
    setPhase('quoting'); setAck(false); setError(null);
    (async () => {
      try {
        const res = await fetchApi<ApiResponse<{ quotes: RenewalQuoteLine[]; totals: Record<string, number> }>>('/renewals/quote', {
          method: 'POST',
          body: JSON.stringify({ domain_ids: ids, years: forYears }),
        });
        if (!mountedRef.current || yearsRef.current !== forYears) return;
        setQuotes(res.data?.quotes ?? []);
        setTotals(res.data?.totals ?? {});
        setPhase('review');
      } catch (e) {
        if (!mountedRef.current || yearsRef.current !== forYears) return;
        setError(e instanceof Error ? e.message : 'Could not get prices');
        setPhase('error');
      }
    })();
  }, [fetchApi, ids, years]);

  const renewable = useMemo(() => quotes.filter((q) => q.renewable && q.renewal_id), [quotes]);
  const blocked = useMemo(() => quotes.filter((q) => !q.renewable), [quotes]);
  const earliestExpiry = useMemo(() => renewable.map((q) => q.expires_at!).sort()[0], [renewable]);

  const confirm = async () => {
    setPhase('running');
    try {
      const res = await fetchApi<ApiResponse<{ batch_id: string; renewals: DomainRenewal[]; rejected: { id: string; reason: string }[] }>>('/renewals/confirm', {
        method: 'POST',
        body: JSON.stringify({ renewal_ids: renewable.map((q) => q.renewal_id) }),
      });
      setRejected(res.data?.rejected ?? []);
      setResults(res.data?.renewals ?? []);
      if (!(res.data?.renewals ?? []).some((r) => OPEN_STATUSES.has(r.status))) {
        setPhase('done');
        onDoneRef.current?.();
      } else {
        pollBatch(res.data!.batch_id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Renewal request failed');
      setPhase('error');
    }
  };

  const pollBatch = (batchId: string) => {
    const tick = async () => {
      try {
        const res = await fetchApi<ApiResponse<DomainRenewal[]>>(`/renewals?batch_id=${encodeURIComponent(batchId)}&limit=500`);
        const rows = res.data ?? [];
        setResults(rows);
        if (rows.length && !rows.some((r) => OPEN_STATUSES.has(r.status))) {
          setPhase('done');
          onDoneRef.current?.();
          return;
        }
      } catch { /* keep polling */ }
      setTimeout(tick, POLL_MS);
    };
    setTimeout(tick, POLL_MS);
  };

  const counts = useMemo(() => ({
    succeeded: results.filter((r) => r.status === 'succeeded').length,
    failed: results.filter((r) => r.status === 'failed' || r.status === 'cancelled').length,
    unknown: results.filter((r) => r.status === 'unknown').length,
    open: results.filter((r) => OPEN_STATUSES.has(r.status)).length,
  }), [results]);

  const busy = phase === 'quoting' || phase === 'running';
  const title = ids.length === 1 ? 'Renew domain' : `Renew ${ids.length} domains`;

  return (
    <Modal
      title={<span className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-slate-400" />{title}</span>}
      size="xl"
      onClose={onClose}
      // Backdrop, Escape and the header × are blocked while quoting or renewing;
      // the footer Close stays available once a batch is running in the background.
      dismissible={!busy}
      bodyClassName="p-5 space-y-4"
      footer={
        phase === 'review' && renewable.length > 0 ? (
          <>
            <button onClick={onClose} className="btn btn-secondary text-sm">Cancel</button>
            <button onClick={confirm} disabled={!ack} className="btn btn-primary text-sm disabled:opacity-50">
              Renew {renewable.length} for {yrs(years)} · {Object.entries(totals).map(([cur, amt]) => money(amt, cur)).join(' + ')}
            </button>
          </>
        ) : (
          <button onClick={onClose} disabled={phase === 'quoting'} className="btn btn-secondary text-sm">Close</button>
        )
      }
    >
          {(phase === 'quoting' || phase === 'review' || phase === 'error') && (
            <div className="flex items-center gap-2">
              <label htmlFor="renew-years" className="text-sm text-slate-600">Term</label>
              <select
                id="renew-years"
                value={years}
                disabled={phase === 'quoting'}
                onChange={(e) => setYears(Number(e.target.value))}
                className="text-sm border border-slate-300 rounded px-2 py-1 bg-surface focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none disabled:opacity-50"
              >
                {TERMS.map((n) => <option key={n} value={n}>{yrs(n)}</option>)}
              </select>
              <span className="text-xs text-slate-400">Porkbun renews 1 year at a time.</span>
            </div>
          )}
          {phase === 'quoting' && (
            <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Getting live renewal prices from the registrars…</div>
          )}

          {phase === 'error' && <div className="text-sm text-red-600">{error}</div>}

          {phase === 'review' && (
            <>
              {renewable.length > 0 && (
                <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Renews each domain for <strong>{yrs(years)}</strong> and <strong>charges its registrar account</strong> immediately. Prices are live quotes, held until {earliestExpiry ? new Date(earliestExpiry).toLocaleTimeString() : 'shortly'}; if a price rises before it runs, that renewal is skipped.</span>
                </div>
              )}
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-1.5 font-medium">Domain</th>
                    <th className="py-1.5 font-medium">Registrar</th>
                    <th className="py-1.5 font-medium text-right">{yrs(years)} price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {renewable.map((q) => (
                    <tr key={q.renewal_id}>
                      <td className="py-1.5 font-mono text-xs">{q.domain_name}</td>
                      <td className="py-1.5 text-slate-600">{registrarName(q.registrar_type)}</td>
                      <td className="py-1.5 text-right">
                        {money(q.amount, q.currency)}
                        {q.note && <div className="text-[11px] text-slate-400">{q.note}</div>}
                      </td>
                    </tr>
                  ))}
                  {blocked.map((q) => (
                    <tr key={q.target} className="text-slate-400">
                      <td className="py-1.5 font-mono text-xs">{q.domain_name ?? q.target}</td>
                      <td className="py-1.5">{q.registrar_type ? registrarName(q.registrar_type) : '—'}</td>
                      <td className="py-1.5 text-right text-xs text-slate-500 max-w-xs">Not renewing: {q.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {renewable.length > 0 ? (
                <>
                  <div className="flex justify-end gap-4 text-sm font-medium text-slate-900">
                    {Object.entries(totals).map(([cur, amt]) => <span key={cur}>Total: {money(amt, cur)}</span>)}
                  </div>
                  <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600" />
                    I understand this charges the registrar account{renewable.length > 1 ? 's' : ''} for {renewable.length} renewal{renewable.length > 1 ? 's' : ''} of {yrs(years)}.
                  </label>
                </>
              ) : (
                <p className="text-sm text-slate-600">None of the selected domains can be renewed from here.</p>
              )}
            </>
          )}

          {(phase === 'running' || phase === 'done') && (
            <>
              {phase === 'running' && counts.open > 0 && (
                <div className="flex items-center gap-2 text-sm text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Renewing… {results.length - counts.open} of {results.length} done. You can close this window; it keeps running.</div>
              )}
              {phase === 'running' && results.length === 0 && (
                <div className="flex items-center gap-2 text-sm text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Renewing…</div>
              )}
              {phase === 'done' && (
                <p className="text-sm text-slate-700">
                  {counts.succeeded} renewed{counts.failed ? ` · ${counts.failed} not renewed` : ''}{counts.unknown ? ` · ${counts.unknown} unknown` : ''}.
                </p>
              )}
              {counts.unknown > 0 && (
                <div className="flex items-start gap-2 text-xs text-orange-800 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">
                  <HelpCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>An <strong>unknown</strong> renewal got no definite answer from the registrar and may have been charged. Check the registrar, then mark it on the domain's page. It won't be retried automatically.</span>
                </div>
              )}
              <ul className="divide-y divide-slate-100 text-sm">
                {results.map((r) => (
                  <li key={r.id} className="py-2 flex items-start gap-2">
                    <StatusIcon status={r.status} />
                    <div className="min-w-0 flex-1">
                      <div className="flex justify-between gap-2">
                        <span className="font-mono text-xs">{r.domain_name}</span>
                        <span className="text-xs text-slate-500">
                          {r.status === 'succeeded' ? `${yrs(r.years)} · ${money(r.charged_amount ?? r.quoted_amount, r.currency)} · expires ${day(r.new_expiry_date)}` : r.status}
                        </span>
                      </div>
                      {r.message && r.status !== 'succeeded' && <div className="text-xs text-slate-500 mt-0.5">{r.message}</div>}
                      {r.order_id && <div className="text-[11px] text-slate-400">Order {r.order_id}</div>}
                    </div>
                  </li>
                ))}
                {rejected.map((r) => (
                  <li key={r.id} className="py-2 text-xs text-slate-500">Skipped {r.id}: {r.reason}</li>
                ))}
              </ul>
            </>
          )}
    </Modal>
  );
}

function StatusIcon({ status }: { status: DomainRenewal['status'] }) {
  if (status === 'succeeded') return <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />;
  if (status === 'unknown') return <HelpCircle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />;
  if (status === 'queued' || status === 'processing') return <Loader2 className="h-4 w-4 text-slate-400 animate-spin mt-0.5 shrink-0" />;
  return <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />;
}
